import { supabase } from '@/integrations/supabase/client';

/**
 * Testa a conectividade com o Supabase
 * @returns Promise<boolean> - true se conectado, false se não
 */
export async function testSupabaseConnection(): Promise<boolean> {
  try {
    console.log('🔄 Testando conectividade com Supabase...');
    
    // Teste simples de conectividade
    const { data, error } = await supabase
      .from('leads')
      .select('id')
      .limit(1);
    
    if (error) {
      console.error('❌ Erro de conectividade Supabase:', error);
      return false;
    }
    
    console.log('✅ Supabase conectado com sucesso');
    return true;
  } catch (error) {
    console.error('❌ Falha na conectividade Supabase:', error);
    return false;
  }
}

/**
 * Executa uma função com retry automático em caso de falha de conexão
 * @param fn - Função a ser executada
 * @param maxRetries - Número máximo de tentativas (padrão: 3)
 * @param delay - Delay entre tentativas em ms (padrão: 2000)
 * @returns Promise com o resultado da função
 */
export async function withRetry<T>(
  fn: () => Promise<T>,
  maxRetries: number = 3,
  delay: number = 2000
): Promise<T> {
  let lastError: Error | null = null;
  
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      console.log(`🔄 Tentativa ${attempt}/${maxRetries}...`);
      return await fn();
    } catch (error) {
      lastError = error as Error;
      console.error(`❌ Tentativa ${attempt} falhou:`, error);
      
      // Se for erro de conexão e não for a última tentativa, aguardar antes de tentar novamente
      if (attempt < maxRetries && isConnectionError(error)) {
        console.log(`⏳ Aguardando ${delay}ms antes da próxima tentativa...`);
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
  }
  
  throw lastError || new Error('Máximo de tentativas excedido');
}

/**
 * Verifica se o erro é relacionado à conexão
 * @param error - Erro a ser verificado
 * @returns boolean
 */
function isConnectionError(error: unknown): boolean {
  if (error instanceof Error) {
    const message = error.message.toLowerCase();
    return (
      message.includes('failed to fetch') ||
      message.includes('err_connection_closed') ||
      message.includes('network error') ||
      message.includes('connection refused') ||
      message.includes('timeout')
    );
  }
  return false;
}

/**
 * Verifica se as variáveis de ambiente do Supabase estão configuradas
 * @returns boolean
 */
export function checkSupabaseConfig(): boolean {
  const url = import.meta.env.VITE_SUPABASE_URL;
  const key = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;
  
  if (!url || !key) {
    console.error('❌ Variáveis de ambiente do Supabase não configuradas');
    console.error('VITE_SUPABASE_URL:', url ? '✅ Configurada' : '❌ Não configurada');
    console.error('VITE_SUPABASE_PUBLISHABLE_KEY:', key ? '✅ Configurada' : '❌ Não configurada');
    return false;
  }
  
  console.log('✅ Configurações do Supabase encontradas');
  return true;
}
